import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", date_of_birth: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Welcome to the pack! Let's get you set up.");
      nav("/welcome");
    } catch (er) {
      setErr(formatApiError(er));
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 bg-[color:var(--cream)] order-2 lg:order-1">
        <div className="w-full max-w-md">
          <a href="https://hoodriveradoptadog.org" target="_blank" rel="noopener noreferrer" data-testid="register-logo-link" className="inline-block mb-8">
            <img src="/logo.png" alt="Hood River Adopt A Dog" className="h-14 w-auto" />
          </a>

          <h1 className="font-display text-4xl mb-2">Become a volunteer.</h1>
          <p className="text-[color:var(--stone)] mb-6">Takes under a minute. You&rsquo;ll sign the waiver right after.</p>

          <button data-testid="register-google-btn" onClick={googleSignIn} className="w-full btn-pill btn-ghost-ink flex items-center justify-center gap-3 mb-4">
            <svg viewBox="0 0 24 24" className="h-5 w-5"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.23 1.5-1.7 4.4-5.35 4.4-3.22 0-5.85-2.67-5.85-5.95 0-3.28 2.63-5.95 5.85-5.95 1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.86 3.9 14.68 3 12 3 6.98 3 2.93 7.03 2.93 12s4.05 9 9.07 9c5.24 0 8.71-3.68 8.71-8.86 0-.6-.06-1.05-.15-1.5z"/></svg>
            Sign up with Google
          </button>

          <div className="my-4 text-center text-xs text-[color:var(--stone)]">or with email</div>

          <form onSubmit={submit} className="space-y-3">
            <input data-testid="register-name-input" required placeholder="Full name" value={form.name} onChange={upd("name")} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white" />
            <input data-testid="register-email-input" type="email" required placeholder="Email" value={form.email} onChange={upd("email")} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white" />
            <input data-testid="register-phone-input" type="tel" required minLength={5} placeholder="Phone number" value={form.phone} onChange={upd("phone")} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white" />
            <div>
              <label htmlFor="register-dob-input" className="block text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] mb-1 ml-4">Date of birth</label>
              <input
                id="register-dob-input"
                data-testid="register-dob-input"
                type="date"
                required
                max={new Date().toISOString().slice(0,10)}
                value={form.date_of_birth}
                onChange={upd("date_of_birth")}
                className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white"
              />
              {(() => {
                const d = form.date_of_birth;
                if (!/^\d{4}-\d{2}-\d{2}$/.test(d || "")) return null;
                const [y, m, dd] = d.split("-").map(Number);
                const t = new Date();
                let a = t.getFullYear() - y;
                if (t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < dd)) a -= 1;
                if (a >= 0 && a < 18) {
                  return (
                    <p className="mt-1 ml-4 text-xs text-[color:var(--red)] font-semibold" data-testid="register-dob-minor-warning">
                      Under 18 — after you create your account, you&rsquo;ll be guided to print a paper waiver for a parent or guardian to sign. You can&rsquo;t sign the waiver online.
                    </p>
                  );
                }
                return null;
              })()}
            </div>
            <input data-testid="register-password-input" type="password" required minLength={6} placeholder="Create password (min 6 chars)" value={form.password} onChange={upd("password")} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] focus:border-[color:var(--ink)] outline-none bg-white" />
            {err && <div data-testid="register-error" className="text-sm text-[color:var(--red)]">{err}</div>}
            <button data-testid="register-submit-btn" type="submit" disabled={busy} className="w-full btn-pill btn-primary disabled:opacity-60">{busy ? "Creating account…" : "Create my account"}</button>
          </form>

          <div className="mt-4 text-sm text-center">
            <Link data-testid="register-login-link" to="/login" className="text-[color:var(--stone)] hover:text-[color:var(--ink)]">Already signed up? <span className="font-semibold text-[color:var(--red)]">Sign in</span></Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1604606363386-dd3f2357ad87?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Rescue dog at play" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--ink)]/80 via-[color:var(--ink)]/25 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-display text-4xl leading-tight max-w-md">A few hours of your week = a lifetime of love.</div>
        </div>
      </div>
    </div>
  );
}
